import type {
  FC,
  MouseEvent,
  ChangeEvent,
  SetStateAction,
  Dispatch,
} from 'react';
import {
  TablePagination,
  Pagination,
  PaginationItem,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import { useTranslation } from 'react-i18next';

const TablePaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: TablePaginationActionsProps) => {
  const onChange = (_event: ChangeEvent<unknown>, nextPage: number) => {
    onPageChange(null, nextPage - 1);
  };

  return (
    <Pagination
      sx={{ p: 0 }}
      color="primary"
      count={Math.ceil(count / rowsPerPage) || 1}
      page={page + 1}
      shape="rounded"
      size="small"
      renderItem={(item) => (
        <PaginationItem
          components={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
          {...item}
        />
      )}
      onChange={onChange}
    />
  );
};

type CustomTablePaginationPropsType = {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  rowsPerPage: number;
  setRowsPerPage: Dispatch<SetStateAction<number>>;
  handleChangePage: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  rowsLength: number;
  colSpan: number;
  rowsPerPageOptions?: number[];
};

export const CustomTablePagination: FC<CustomTablePaginationPropsType> = ({
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  handleChangePage,
  rowsLength,
  colSpan,
  rowsPerPageOptions = [20, 50, 100],
}) => {
  const { t } = useTranslation();
  const handleChangeRowsPerPage = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  return (
    <Stack
      direction="row"
      sx={{ minWidth: '100%', mt: 1.5 }}
      justifyContent="center"
    >
      <TablePagination
        component="div"
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.hairline}`,
          '&> .MuiToolbar-root': {
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: 1,
            py: 1.5,
            minHeight: 52,
            '& .MuiTablePagination-spacer': { display: 'none' },
            '& .MuiTablePagination-selectLabel': { display: 'none' },
            '& .MuiInputBase-root': {
              marginRight: 0,
              '& .MuiSelect-select': { paddingLeft: '12px' },
            },
            '& .MuiTablePagination-displayedRows': {
              margin: 0,
              fontSize: '0.8125rem',
              color: 'text.secondary',
            },
            '& .MuiTablePagination-actions': { marginLeft: 0 },
          },
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        labelDisplayedRows={({ from, to, count }) =>
          `${from} ${t('to')} ${to} ${t('of')} ${count} ${t('entries')}`
        }
        colSpan={colSpan}
        count={rowsLength}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </Stack>
  );
};
